import { isAgentsEmbed } from '@/lib/agents-embed';
import { createContext, useContext, useEffect, useState } from 'react';
import { useParams, useLocation, useSearchParams, Link, Outlet, Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AgentBuilderFocus, AgentSchedule } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { SetupChecklist } from './_components/setup-checklist';
import { BuilderPanel } from '../_components/builder/builder-panel';
import { useAgentEditor, type LayoutAgent } from './_components/use-agent-editor';
import { useFlag } from '@/contexts/flags-context';
import { AgentSettingsFrame } from '@/features/agent-workspace/AgentSettingsFrame';
import { formatWhen } from './rituals/format';
import { useTranslations } from '@/i18n/use-translations';
import { PageHeader } from '@/components/ui/page-header';
import { FlaskConical, Plug, Sparkles, CalendarClock, Brain, SlidersHorizontal, Wand2, X } from 'lucide-react';

const TABS = [
  { key: 'behavior', icon: Sparkles, labelKey: 'tabBehavior' },
  { key: 'connections', icon: Plug, labelKey: 'tabConnections' },
  { key: 'rituals', icon: CalendarClock, labelKey: 'tabRituals' },
  { key: 'memory', icon: Brain, labelKey: 'tabMemory' },
  { key: 'test', icon: FlaskConical, labelKey: 'tabTest' },
  { key: 'advanced', icon: SlidersHorizontal, labelKey: 'tabAdvanced' },
] as const;

const TAB_FOCUS: Record<string, AgentBuilderFocus> = {
  edit: 'prompt', personality: 'soul', behavior: 'soul', advanced: 'prompt',
  rituals: 'rituals', memory: 'knowledge', connections: 'all',
};


function nextRitual(schedules: AgentSchedule[] | null, autonomyOn: boolean): AgentSchedule | null {
  if (!schedules || !autonomyOn) return null;
  return schedules.filter((s) => s.kind !== 'followup' && s.enabled && s.nextRunAt)
    .sort((a, b) => a.nextRunAt!.localeCompare(b.nextRunAt!))[0] ?? null;
}

type AgentLayoutContextValue = { agent: LayoutAgent | null; refresh: () => void };
const AgentLayoutContext = createContext<AgentLayoutContextValue | null>(null);

export function useAgentLayout() {
  const context = useContext(AgentLayoutContext);
  if (!context) throw new Error('useAgentLayout must be used inside AgentLayout');
  return context;
}

export function AgentLayout() {
  const t = useTranslations('agents');
  const assistantEnabled = useFlag('assistant-beta');
  const { i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const beta = useFlag('agent-beta-theme');
  const { agent, schedules, refresh, revision: refreshVersion, identity } = useAgentEditor(id);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [appliedRevision, setAppliedRevision] = useState(0);
  const onBuilderApplied = () => { refresh(); setAppliedRevision((value) => value + 1); };
  const createdName = (location.state as { builderCreated?: string } | null)?.builderCreated;
  const [toast, setToast] = useState<string | null>(createdName ?? null);
  const welcome = searchParams.get('welcome') === '1';
  const lastSegment = location.pathname.split('/').filter(Boolean).pop();
  const activeTab = TABS.find((tab) => tab.key === lastSegment)?.key ?? 'behavior';

  useEffect(() => { if (createdName) setToast(createdName); }, [createdName]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  const agentName = agent?.name ?? '';
  const upcoming = nextRitual(schedules, agent?.settings?.autonomy?.enabled !== false);
  const upcomingLabel = upcoming ? formatWhen(upcoming.nextRunAt, upcoming.timezone, i18n.language) : null;
  const dismissWelcome = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('welcome');
    setSearchParams(next, { replace: true });
  };

  if (!agent) return <p role="status" className="p-8">Cargando agente…</p>;

  if (agent?.kind === 'assistant') {
    if (!assistantEnabled) return <Navigate to="/agents" replace />;
    return (
      <div className="flex h-full flex-col">
        <PageHeader
          className="px-4 pt-6 sm:px-6"
          title={agent.name}
          description="Assistant Beta"
          breadcrumbs={[{ label: t('title'), href: '/agents' }, { label: agent.name }]}
        />
        <main className="flex flex-1 items-center justify-center p-6">
          <section className="max-w-lg rounded-xl border border-violet-500/30 bg-violet-500/5 p-6 text-center">
            <h2 className="text-lg font-semibold">Gateway Assistant pendiente</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Este agente está reservado para la ejecución mediante el gateway Assistant. Los editores Cloud no están disponibles.
            </p>
          </section>
        </main>
      </div>
    );
  }

  if (beta && id) {
    return <>
      <AgentSettingsFrame id={id} name={agentName} status={agent?.status} avatar={agent?.avatar} active={activeTab} search={location.search} onAssistant={() => setBuilderOpen(true)}
        setup={agent && <SetupChecklist agent={agent} schedules={schedules} refreshKey={String(refreshVersion)} highlight={welcome} />}
        notice={<>{welcome && agent && <div role="status" className="mb-4 rounded-lg border border-violet/20 bg-violet/5 p-3 text-sm"><span>{t('welcomeBanner', { name: agent.name })}</span><button type="button" onClick={dismissWelcome} className="ml-3 underline">{t('welcomeDismiss')}</button></div>}{toast && <div role="status" className="mb-4 p-3 text-sm">{t('builder.createdToast', { name: toast })}<button type="button" onClick={() => setToast(null)} className="ml-3 underline">{t('builder.dismiss')}</button></div>}</>}>
        <AgentLayoutContext.Provider value={{ agent, refresh }}><div key={`${identity}:${appliedRevision}`}><Outlet /></div></AgentLayoutContext.Provider>
      </AgentSettingsFrame>
      <BuilderPanel open={builderOpen} onOpenChange={setBuilderOpen} agentId={id} agentName={agentName} focus={TAB_FOCUS[lastSegment ?? ''] ?? 'all'} onApplied={onBuilderApplied} />
    </>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 sm:px-6 pt-6 pb-0">
        <PageHeader
          className="mb-0"
          title={agentName || t('editAgent')}
          description={t('editAgentDesc')}
          breadcrumbs={[{ label: t('title'), href: '/agents' }, { label: agentName || t('editAgent') }]}
          actions={<div className="flex flex-wrap items-center gap-2">
            {agent && <StatusBadge variant={agent.status === 'active' ? 'active' : 'draft'}>{t(`status.${agent.status}`)}</StatusBadge>}
            {upcomingLabel && <span className="text-xs text-muted-foreground">{t('nextRitual', { when: upcomingLabel })}</span>}
            <Button variant="outline" size="sm" onClick={() => setBuilderOpen(true)}><Wand2 className="h-4 w-4 mr-1.5" />{t('builder.open')}</Button>
          </div>}
        />
        {welcome && agent && <div role="status" className="mt-3 flex items-start justify-between gap-3 rounded-md border border-green-500/30 bg-green-500/5 p-3 text-sm"><span>{t('welcomeBanner', { name: agent.name })}</span><button type="button" onClick={dismissWelcome} className="shrink-0 text-xs text-muted-foreground underline underline-offset-4">{t('welcomeDismiss')}</button></div>}
        {toast && <div role="status" className="mt-3 flex items-center justify-between gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm"><span>{t('builder.createdToast', { name: toast })}</span><button type="button" onClick={() => setToast(null)} aria-label={t('builder.dismiss')} className="rounded p-0.5 hover:bg-emerald-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"><X className="h-3.5 w-3.5" /></button></div>}
        {!isAgentsEmbed() && agent && <SetupChecklist agent={agent} schedules={schedules} refreshKey={String(refreshVersion)} highlight={welcome} />}
        <div className="flex gap-1 mt-4 border-b border-border overflow-x-auto">
          {TABS.filter((tab) => !isAgentsEmbed() || ['behavior', 'advanced', 'test'].includes(tab.key)).map((tab) => { const Icon = tab.icon; const isActive = activeTab === tab.key; return <Link key={tab.key} to={`/agents/${id}/${tab.key}${location.search}`} aria-current={isActive ? 'page' : undefined} className={`flex shrink-0 items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}><Icon className="h-3.5 w-3.5" />{t(tab.labelKey)}</Link>; })}
        </div>
      </div>
      <div className="flex-1 min-h-0" key={refreshVersion}><AgentLayoutContext.Provider value={{ agent, refresh }}><Outlet /></AgentLayoutContext.Provider></div>
      {id && <BuilderPanel open={builderOpen} onOpenChange={setBuilderOpen} agentId={id} agentName={agentName} focus={TAB_FOCUS[lastSegment ?? ''] ?? 'all'} onApplied={onBuilderApplied} />}
    </div>
  );
}
